import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { apiPutStoryById, APIPutStoryPayload, Story, StoryMetadata } from '@/shared/api';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { INPUT_DEBOUNCE_TIME } from '@/shared/utils';

type StorySettingsDialogProps = {
  metadata: StoryMetadata;
  storyId: string;
};

export const StorySettingsDialog = ({
  metadata: propsMetadata,
  storyId,
}: StorySettingsDialogProps) => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const [metadata, setMetadata] = useState<StoryMetadata>(propsMetadata);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const mutation = useMutation<Story, Error, { id: string; payload: APIPutStoryPayload }>(
    {
      mutationFn: (data) => {
        return apiPutStoryById(data.id, data.payload).then((data) => data.data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: storyByIdQueryOptions(storyId).queryKey,
        });
      },
    },
  );

  const handleChange = (key: keyof StoryMetadata, value: string) => {
    setMetadata((prev) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      const updatedMetadata = { ...prev, [key]: value };

      debounceTimeoutRef.current = setTimeout(() => {
        mutation.mutate({ id: storyId, payload: { metadata: updatedMetadata } });
      }, INPUT_DEBOUNCE_TIME);

      return updatedMetadata;
    });
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="icon">
          <Settings />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({ id: 'storySettings.dialogTitle' })}
          </DialogTitle>
          <DialogDescription>
            {intl.formatMessage({ id: 'storySettings.dialogDescription' })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4">
          <div>
            <Label>{intl.formatMessage({ id: 'storySettings.language' })}</Label>

            <Select
              value={metadata.locale ?? 'en'}
              onValueChange={(value) => handleChange('locale', value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="ua">Українська</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{intl.formatMessage({ id: 'storySettings.fontSize' })}</Label>

            <Select
              value={metadata.fontSize ?? '16px'}
              onValueChange={(value) => handleChange('fontSize', value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="16px">16px</SelectItem>
                <SelectItem value="20px">20px</SelectItem>
                <SelectItem value="24px">24px</SelectItem>
                <SelectItem value="28px">28px</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">{intl.formatMessage({ id: 'button.save' })}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
